<template>
    <div v-if="serviceOnline" class="task-widget" :style="widgetStyle" ref="widget">

        <!-- FAB colapsado -->
        <div v-if="!expanded" class="task-widget__collapsed" title="Tarefas">
            <div class="task-widget__fab-handle" @mousedown.prevent="startDrag">
                <i class="fas fa-arrows-alt"></i>
            </div>
            <div class="task-widget__fab" @click="expand">
                <img src="/imgs/icons/tasks_icon.png" alt="tasks" style="width:34px;height:34px;" />
                <span v-if="notificationSummary.count > 0" class="task-widget__fab-badge"
                    :class="notificationSummary.high ? 'task-widget__fab-badge--high' : 'task-widget__fab-badge--low'">
                    {{ notificationSummary.count > 99 ? '99+' : notificationSummary.count }}
                </span>
            </div>
        </div>

        <!-- Painel expandido -->
        <div v-else class="task-widget__panel">

            <!-- Header (drag handle) -->
            <div class="task-widget__header" @mousedown.prevent="startDrag"
                :class="{ 'task-widget__header--dragging': isDragging }">
                <div class="task-widget__header-title">
                    <i class="fas fa-tasks mr-2"></i>
                    <span>TAREFAS</span>
                    <span class="task-widget__header-count">{{ totalRows }}</span>
                </div>
                <div class="task-widget__header-actions">
                    <button class="task-widget__icon-btn" title="Nova tarefa" @mousedown.stop @click="openCreate">
                        <i class="fas fa-plus"></i>
                    </button>
                    <button class="task-widget__icon-btn" title="Ver todas" @mousedown.stop @click="goToTasks">
                        <i class="fas fa-external-link-alt"></i>
                    </button>
                    <button class="task-widget__icon-btn" title="Minimizar" @mousedown.stop @click="collapse">
                        <i class="fas fa-minus"></i>
                    </button>
                </div>
            </div>

            <!-- Busca -->
            <div class="task-widget__search">
                <input type="text" v-model="search" placeholder="Pesquisar tarefa..." class="task-widget__search-input"
                    @mousedown.stop />
                <button class="task-widget__mine-btn" :class="{ 'task-widget__mine-btn--active': onlyMine }"
                    :title="onlyMine ? 'Mostrar todas tarefas' : 'Mostrar apenas minhas tarefas'" @mousedown.stop
                    @click="onlyMine = !onlyMine">
                    <i class="fas fa-user"></i>
                    <span v-if="onlyMine && groupNotification.show" class="task-widget__mine-dot"
                        :class="groupNotification.high ? 'task-widget__mine-dot--high' : 'task-widget__mine-dot--low'"></span>
                </button>
            </div>

            <!-- Lista -->
            <div class="task-widget__list" @mousedown.stop>
                <div v-if="loading" class="task-widget__state">
                    <i class="fas fa-spinner fa-spin"></i> Carregando...
                </div>

                <div v-else-if="tasks.length === 0" class="task-widget__state">
                    Nenhuma tarefa encontrada.
                </div>

                <div v-else v-for="task in tasks" :key="task._id" class="task-widget__item" :class="{
                    'task-widget__item--notify-high': task.new_notification && task.new_notification.high,
                    'task-widget__item--notify-low': task.new_notification && !task.new_notification.high,
                }" @click="openEdit(task)">
                    <div class="task-widget__item-title">
                        <span v-if="task.protocol" class="task-widget__protocol">
                            <i class="fas fa-hashtag"></i>{{ task.protocol }}
                        </span>

                        <span v-if="task.reason" :tittle="formatReasonText(task)" class="task-widget__reason" :class="{ 'task-widget__reason--itau': task.is_itau }">
                            {{ formatReasonText(task) }}
                        </span>
                        
                        <span v-if="task.started_at && task.sla" class="task-widget__sla"
                            :class="slaInfo(task).expired ? 'task-widget__sla--expired' : slaInfo(task).alert ? 'task-widget__sla--alert' : 'task-widget__sla--ok'">
                            <i class="fas fa-clock"></i>
                            {{ slaInfo(task).label }}
                        </span>
                    </div>

                    <div class="task-widget__item-meta">
                        <span v-if="task.status" class="task-widget__badge" :style="dinamicStyle(task.status.color)">
                            <i class="fas fa-tags"></i> {{ textSizeLimit(capitalize(task.status.name), 12) }}
                        </span>

                        <span v-if="task.priority" class="task-widget__badge" :style="dinamicStyle(task.priority.color)">
                            <i class="fas fa-bullseye"></i> {{ capitalize(task.priority.name) }}
                        </span>

                        <span v-if="task.owner_group" class="task-widget__owner">
                            <i class="fas fa-users"></i> {{ textSizeLimit(task.owner_group.name, 10) }}
                        </span>

                        <span v-if="task.owner_user" class="task-widget__owner">
                            <i class="fas fa-user"></i> {{ textSizeLimit(task.owner_user.name, 10) }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Paginação -->
            <div class="task-widget__pagination" @mousedown.stop>
                <span class="task-widget__footer-info">
                    {{ tasks.length }} de {{ totalRows }} tarefas
                </span>

                <div class="task-widget__pagination-controls">
                    <button class="task-widget__page-btn" @click="prevPage" :disabled="currentPage <= 1 || loading">
                        <i class="fas fa-chevron-left"></i>
                    </button>
                    <span class="task-widget__page-info">{{ currentPage }} / {{ totalPages }}</span>
                    <button class="task-widget__page-btn" @click="nextPage"
                        :disabled="currentPage >= totalPages || loading">
                        <i class="fas fa-chevron-right"></i>
                    </button>
                </div>

                <a @click="goToTasks" class="task-widget__footer-link" :disabled="hasPermissionTaskManager">
                    Ver todas <i class="fas fa-arrow-right"></i>
                </a>
            </div>

            <!-- Footer -->
            <div class="task-widget__footer" @mousedown.stop>
                <div class="task-widget__footer-drag" @mousedown.prevent="startDrag">
                    <i class="fas fa-grip-lines"></i>
                </div>
            </div>
        </div>

        <!-- Toasts de tarefa finalizada (modo expandido, à esquerda do painel) -->
        <transition-group v-if="expanded" name="task-toast" tag="div" class="task-widget__toasts">
            <div v-for="toast in toasts" :key="toast.id" class="task-widget__toast">
                <div class="task-widget__toast-icon">
                    <i class="fas fa-check-circle"></i>
                </div>
                <div class="task-widget__toast-content">
                    <div class="task-widget__toast-title">Tarefa finalizada</div>
                    <div class="task-widget__toast-msg" v-html="toast.message"></div>
                </div>
                <button class="task-widget__toast-close" @click="removeToast(toast.id)">
                    <i class="fas fa-times"></i>
                </button>
            </div>
        </transition-group>

        <TaskFormModal ref="formModal" modal-id="task-form-modal-widget" :priorities-list="prioritiesList"
            :statuses-list="statusesList" :groups-list="groupsList" :users-list="usersList" :reasons-list="reasonsList"
            @saved="onSaved" />

        <TaskDetailsModal ref="detailsModal" modal-id="task-details-modal-widget" :priorities-list="prioritiesList"
            :users-list="usersList" :reasons-list="reasonsList" @updateTaskTable="onSaved"
            @notificationRead="onNotificationRead" />
    </div>
</template>

<script>
import TaskFormModal from './TaskFormModal.vue';
import TaskDetailsModal from './TaskDetailsModal.vue';
import { textSizeLimit } from '../../helpers/utils.js';
import { getTaskSocket } from './connections/taskSocketConnection.js';

import { taskManagerAxios, TASKMANAGER_URL } from './connections/taskApiConnection.js';
const POS_KEY = 'taskWidget_pos';
const EXPANDED_KEY = 'taskWidget_expanded';
const ONLY_MINE_KEY = 'taskWidget_onlyMine';

export default {
    name: 'TaskManagerWidget',
    components: { TaskFormModal, TaskDetailsModal },
    data() {
        return {
            expanded: JSON.parse(localStorage.getItem(EXPANDED_KEY) ?? 'false'),
            position: this.loadPosition(),
            isDragging: false,
            dragOffset: { x: 0, y: 0 },
            tasks: [],
            totalRows: 0,
            currentPage: 1,
            perPage: 5,
            loading: false,
            search: '',
            onlyMine: JSON.parse(localStorage.getItem(ONLY_MINE_KEY) ?? 'true'),
            searchDebounce: null,
            prioritiesList: [],
            statusesList: [],
            groupsList: [],
            usersList: [],
            reasonsList: [],
            now: Date.now(),
            slaInterval: null,
            notificationSummary: { count: 0, high: false },
            groupNotification: { show: false, high: false },
            taskSocket: null,
            toasts: [],
            toastCounter: 0,
            serviceOnline: false,
        };
    },
    computed: {
        totalPages() {
            return Math.max(1, Math.ceil(this.totalRows / this.perPage));
        },
        widgetStyle() {
            return {
                position: 'fixed',
                right: (window.innerWidth - this.position.x - 52) + 'px',
                bottom: (window.innerHeight - this.position.y - 52) + 'px',
                zIndex: 9999,
            };
        },
    },
    async created() {
        const online = await this.checkHealth();
        if (!online) return;
        this.fetchNotificationSummary();
        this.fetchMeta();
        if (this.expanded) this.fetchTasks();
    },
    mounted() {
        this.clampToViewport();
        window.addEventListener('resize', this.onResize);
        this.slaInterval = setInterval(() => { this.now = Date.now(); }, 1000);
        this.setupTaskSocket();
        if (Notification.permission === 'default') Notification.requestPermission();
    },
    beforeDestroy() {
        document.removeEventListener('mousemove', this.onDrag);
        document.removeEventListener('mouseup', this.stopDrag);
        window.removeEventListener('resize', this.onResize);
        clearInterval(this.slaInterval);
        if (this.taskSocket) {
            this.taskSocket.off('task.created', this.onTaskCreated);
            this.taskSocket.off('task.updated', this.onTaskUpdated);
            this.taskSocket.off('task.notification', this.onTaskNotification);
            this.taskSocket.off('task.closed', this.onTaskClosed);
        }
    },
    methods: {
        textSizeLimit,
        async checkHealth() {
            try {
                const { data } = await axios.get(`${TASKMANAGER_URL}/health`);
                this.serviceOnline = data?.status === 'ok';
            } catch {
                this.serviceOnline = false;
            }
            return this.serviceOnline;
        },
        capitalize(str) {
            if (!str) return '';
            return str.charAt(0).toUpperCase() + str.slice(1);
        },
        // ── Drag ──────────────────────────────────────────────────────────────
        startDrag(e) {
            this.isDragging = true;
            this.hasMoved = false;
            this.dragOffset = {
                x: e.clientX - this.position.x,
                y: e.clientY - this.position.y,
            };
            document.addEventListener('mousemove', this.onDrag);
            document.addEventListener('mouseup', this.stopDrag);
        },
        dinamicStyle(color) {
            return {
                color,
                border: color ? `1px solid ${color}` : '1px solid #ccc',
            };
        },
        onDrag(e) {
            if (!this.isDragging) return;
            const newX = Math.min(Math.max(0, e.clientX - this.dragOffset.x), window.innerWidth - 52);
            const newY = Math.min(Math.max(0, e.clientY - this.dragOffset.y), window.innerHeight - 52);
            this.position = { x: newX, y: newY };
        },
        stopDrag() {
            if (!this.isDragging) return;
            this.isDragging = false;
            document.removeEventListener('mousemove', this.onDrag);
            document.removeEventListener('mouseup', this.stopDrag);
            this.savePosition();
        },
        // ── Expand / Collapse ─────────────────────────────────────────────────
        expand() {
            this.expanded = true;
            localStorage.setItem(EXPANDED_KEY, 'true');
            this.fetchTasks();
        },
        collapse() {
            this.expanded = false;
            localStorage.setItem(EXPANDED_KEY, 'false');
        },
        goToTasks() {
            if (this.hasPermissionTaskManager()) return window.location.href = '/task_manager';
            Swal.fire({
                title: 'Você não tem permissão para acessar o modulo de tarefas!',
                type: "error",
                toast: true,
                timer: 3000,
                showConfirmButton: false,
                position: 'top'
            });
        },
        hasPermissionTaskManager() {
            const permission = window?.user?.getPermission() ?? [];
            return permission.includes("task_manager");
        },
        // ── Dados ─────────────────────────────────────────────────────────────
        async fetchNotificationSummary() {
            try {
                const { data } = await taskManagerAxios.get('/tasks/notifications/summary', {
                    params: {
                        onlyOpen: true,
                        search: this.search || null,
                        owner_user_id: user.getIdPrefix(),
                        onlyMine: this.onlyMine || null,
                    }
                });
                const wasHigh = this.notificationSummary.high;
                this.notificationSummary = { count: data.count ?? 0, high: data.high ?? false };
                if (!wasHigh && this.notificationSummary.high) this.playNotificationSound();

                if (this.onlyMine) {
                    const { data: groupData } = await taskManagerAxios.get('/tasks/notifications/summary', {
                        params: {
                            onlyOpen: true,
                            owner_user_id: user.getIdPrefix(),
                            onlyMine: false,
                        }
                    });
                    const hasHidden = (groupData.count ?? 0) > (data.count ?? 0);
                    this.groupNotification = { show: hasHidden, high: hasHidden && (groupData.high ?? false) };
                } else {
                    this.groupNotification = { show: false, high: false };
                }
            } catch (e) {
                console.error('[TaskWidget] fetchNotificationSummary:', e);
            }
        },
        async fetchTasks() {
            this.loading = true;
            try {
                const { data } = await taskManagerAxios.get('/tasks', {
                    params: {
                        perPage: this.perPage,
                        page: this.currentPage,
                        sortBy: 'updatedAt',
                        sortDesc: true,
                        onlyOpen: true,
                        search: this.search || null,
                        owner_user_id: user.getIdPrefix(),
                        onlyMine: this.onlyMine || null,
                    },
                });
                this.tasks = data.data ?? [];
                this.totalRows = data.total ?? 0;
                this.fetchNotificationSummary();
            } catch (e) {
                console.error('[TaskWidget] fetchTasks:', e);
            } finally {
                this.loading = false;
            }
        },
        prevPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
                this.fetchTasks();
            }
        },
        nextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
                this.fetchTasks();
            }
        },
        async fetchMeta() {
            try {
                const [priorities, statuses, groups, users, reasons] = await Promise.all([
                    taskManagerAxios.get('/priorities'),
                    taskManagerAxios.get('/statuses'),
                    taskManagerAxios.get('/groups'),
                    taskManagerAxios.get('/users'),
                    taskManagerAxios.get('/reasons'),
                ]);
                this.prioritiesList = priorities.data ?? [];
                this.statusesList = statuses.data ?? [];
                this.groupsList = groups.data ?? [];
                this.usersList = users.data ?? [];
                this.reasonsList = reasons.data ?? [];
            } catch (e) {
                console.error('[TaskWidget] fetchMeta:', e);
            }
        },
        // ── CRUD ──────────────────────────────────────────────────────────────
        openCreate() {
            this.$refs.formModal.open();
        },
        async openEdit(task) {
            try {
                if (!task.comments || !task.timeline) {
                    Swal.fire({
                        title: "Carregando dados da tarefa.",
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        showConfirmButton: false,
                        html: '<div class="d-flex justify-content-center my-3"><i class="fa fa-spinner fa-spin fa-3x"></i></div><p>Por favor, aguarde...</p>',
                    });
                    const { data } = await taskManagerAxios.get(`/tasks/${task._id}`);
                    if (!data) {
                        Swal.fire({ type: 'error', text: 'Falha ao obter detalhes da tarefa!' });
                        return;
                    }
                    Object.assign(task, data);
                }
                Swal.close();
                this.$refs.detailsModal.openModal(task);
            } catch (error) {
                Swal.close();
                console.error(error);
                Swal.fire({ type: 'error', text: 'Falha ao obter detalhes da tarefa!' });
            }
        },
        onSaved(task) {
            this.addTaskToList(task);
        },
        // ── Handlers de broadcast (acionados por TasksPage) ───────────────────
        addTaskToList(task) {
            const idx = this.tasks.findIndex(t => t._id === task._id);

            const removeFromList = () => {
                if (idx !== -1) {
                    this.tasks.splice(idx, 1);
                    this.totalRows = Math.max(0, this.totalRows - 1);
                }
            };

            if (task.closed_at) return removeFromList();
            const userId = user.getIdPrefix();
            const isOwner = task.owner_user?._id == userId;
            const isGroupMember = task.owner_group?.members?.some(m => (typeof m === 'object' ? m.id : m) == userId);
            if (!isOwner && !isGroupMember) return removeFromList();
            if (idx !== -1) return this.$set(this.tasks, idx, task);
            this.tasks.unshift(task);
            if (this.tasks.length > this.perPage) this.tasks.pop();
            this.totalRows++;
        },
        showClosedToast(task) {
            const id = ++this.toastCounter;
            this.toasts.push({ id, message: `<a href="/task_manager?protocol=${task.protocol}" target="_blank">#${task.protocol}</a>` });
            setTimeout(() => this.removeToast(id), 6000);
        },
        removeToast(id) {
            const idx = this.toasts.findIndex(t => t.id === id);
            if (idx !== -1) this.toasts.splice(idx, 1);
        },
        setupTaskSocket() {
            this.taskSocket = getTaskSocket();
            this.taskSocket.on('task.created', this.onTaskCreated);
            this.taskSocket.on('task.updated', this.onTaskUpdated);
            this.taskSocket.on('task.notification', this.onTaskNotification);
            this.taskSocket.on('task.closed', this.onTaskClosed);
        },
        onTaskCreated(task) {
            if (this.tasks.find(t => t._id === task._id)) return;

            const userId = user.getIdPrefix();
            if (this.onlyMine && task.owner_user?._id != userId) return;

            if (this.search) {
                const rx = new RegExp(this.search, 'i');
                const matches = rx.test(task.title) || rx.test(task.description) ||
                    rx.test(task.protocol) || rx.test(task.ref_protocol) || rx.test(task.channel);
                if (!matches) return;
            }

            this.totalRows++;
            this.currentPage = 1;
            this.tasks.unshift(task);
            this.tasks.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
            if (this.tasks.length > this.perPage) this.tasks.pop();
            if (task.new_notification?.high === true) this.playNotificationSound();
            this.fetchNotificationSummary();
        },
        onTaskUpdated(task) {
            this.addTaskToList(task);
            this.fetchNotificationSummary();
        },
        onTaskClosed(task) {
            this.showClosedToast(task);
            this.addTaskToList({ ...task, closed_at: task.closed_at ?? new Date().toISOString() });
            this.fetchNotificationSummary();
        },
        onTaskNotification(task) {
            const idx = this.tasks.findIndex(t => t._id === task._id);
            if (idx !== -1) this.$set(this.tasks, idx, task);
            if (task.new_notification?.high === true) this.playNotificationSound();
            this.fetchNotificationSummary();
        },
        onTaskDeleted(id) {
            const before = this.tasks.length;
            this.tasks = this.tasks.filter(t => t._id !== id);
            if (this.tasks.length < before) this.totalRows = Math.max(0, this.totalRows - 1);
        },
        onNotificationRead(taskId) {
            const idx = this.tasks.findIndex(t => t._id === taskId);
            if (idx !== -1) this.$set(this.tasks[idx], 'new_notification', null);
            this.fetchNotificationSummary();
        },
        // ── Notificação sonora ────────────────────────────────────────────────
        playNotificationSound() {
            try {
                const audio = new Audio('/sounds/task-notification.mp3');
                audio.play();
            } catch (e) {
                console.warn('[TaskWidget] playNotificationSound:', e);
            }
        },
        // ── SLA ───────────────────────────────────────────────────────────────
        slaInfo(task) {
            const sla = task?.priority?.time ?? task.sla;
            const totalMs = sla * 1000;
            const startedAt = new Date(task.started_at).getTime();
            const expiresAt = startedAt + totalMs;
            const diff = expiresAt - this.now;
            const abs = Math.abs(diff);
            const h = Math.floor(abs / 3600000);
            const m = Math.floor((abs % 3600000) / 60000);
            const s = Math.floor((abs % 60000) / 1000);
            const label = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
            const expired = diff < 0;
            const percentAlert = task.priority?.percent_alert;
            const alert = !expired && percentAlert > 0
                ? (this.now - startedAt) >= (totalMs * percentAlert / 100)
                : false;
            return { expired, alert, label };
        },
        // ── Persistência ──────────────────────────────────────────────────────
        loadPosition() {
            try {
                const saved = JSON.parse(localStorage.getItem(POS_KEY));
                if (saved?.x !== undefined && saved?.y !== undefined) return saved;
            } catch (_) { }
            return {
                x: Math.max(0, (window.innerWidth || 1200) - 370),
                y: Math.max(0, (window.innerHeight || 800) - 75),
            };
        },
        savePosition() {
            localStorage.setItem(POS_KEY, JSON.stringify(this.position));
        },
        clampToViewport() {
            this.position = {
                x: Math.min(Math.max(0, this.position.x), window.innerWidth - 52),
                y: Math.min(Math.max(0, this.position.y), window.innerHeight - 52),
            };
        },
        onResize() {
            this.clampToViewport();
            this.savePosition();
        },
        formatReasonText(task) {
            const text = task.is_itau ? `${task.reason} (Itaú)` : task.reason;
            return this.textSizeLimit(text, 50);
        }
    },
    watch: {
        search() {
            clearTimeout(this.searchDebounce);
            this.searchDebounce = setTimeout(() => {
                this.currentPage = 1;
                this.fetchTasks();
            }, 500);
        },
        onlyMine(val) {
            localStorage.setItem(ONLY_MINE_KEY, JSON.stringify(val));
            this.currentPage = 1;
            this.fetchTasks();
        },
    },
}
</script>

<style scoped>
/* ── FAB colapsado ─────────────────────────────────────────────────── */
.task-widget__collapsed {
    position: relative;
    display: inline-block;
}

.task-widget__fab-handle {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #1c869a;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    font-size: 9px;
    user-select: none;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
    transition: background 0.2s;
    z-index: 1;
}

.task-widget__fab-handle:hover {
    background: #166d7e;
}

.task-widget__fab-handle:active {
    cursor: grabbing;
}

/* ── FAB ───────────────────────────────────────────────────────────── */
.task-widget__fab {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #1c869a;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.28);
    position: relative;
    transition: background 0.2s, transform 0.15s;
    user-select: none;
}

.task-widget__fab * {
    pointer-events: none;
    user-select: none;
    -webkit-user-drag: none;
}

.task-widget__fab:hover {
    background: #166d7e;
    transform: scale(1.07);
}

.task-widget__fab:active {
    cursor: grabbing;
    transform: scale(1.0);
}

.task-widget__fab-badge {
    position: absolute;
    bottom: -4px;
    left: -4px;
    color: #fff;
    border-radius: 10px;
    font-size: 10px;
    font-weight: bold;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.task-widget__fab-badge--high {
    background: #e53935;
    animation: task-badge-pulse 1.4s ease-in-out infinite;
}

.task-widget__fab-badge--low {
    background: #f9a825;
}

@keyframes task-badge-pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(229, 57, 53, 0.7);
    }

    70% {
        box-shadow: 0 0 0 7px rgba(229, 57, 53, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(229, 57, 53, 0);
    }
}

/* ── Painel ────────────────────────────────────────────────────────── */
.task-widget__panel {
    width: 340px;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* ── Header ────────────────────────────────────────────────────────── */
.task-widget__header {
    background: #1c869a;
    color: #fff;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: grab;
    user-select: none;
}

.task-widget__header--dragging {
    cursor: grabbing;
}

.task-widget__header-title {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 14px;
    gap: 4px;
}

.task-widget__header-count {
    background: rgba(255, 255, 255, 0.25);
    border-radius: 10px;
    font-size: 11px;
    padding: 1px 7px;
    margin-left: 4px;
}

.task-widget__header-actions {
    display: flex;
    gap: 4px;
}

.task-widget__icon-btn {
    background: none;
    border: none;
    color: #fff;
    cursor: pointer;
    padding: 3px 6px;
    border-radius: 4px !important;
    font-size: 13px;
    line-height: 1;
    transition: background 0.15s;
}

.task-widget__icon-btn:hover {
    background: rgba(255, 255, 255, 0.2);
}

/* ── Busca ─────────────────────────────────────────────────────────── */
.task-widget__search {
    padding: 8px 10px;
    border-bottom: 1px solid #eee;
    display: flex;
    align-items: center;
    gap: 6px;
}

.task-widget__search-input {
    flex: 1;
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 5px 10px;
    font-size: 13px;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;
}

.task-widget__search-input:focus {
    border-color: #1c869a;
}

.task-widget__mine-btn {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: #fff;
    height: 32px;
    color: #888;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    position: relative;
}

.task-widget__mine-dot {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    border: 1px solid #fff;
    pointer-events: none;
}

.task-widget__mine-dot--high {
    background: #e53935;
    animation: task-badge-pulse 1.4s ease-in-out infinite;
}

.task-widget__mine-dot--low {
    background: #f9a825;
}

.task-widget__mine-btn:hover {
    border-color: #1c869a;
    color: #1c869a;
}

.task-widget__mine-btn--active {
    background: #1c869a;
    border-color: #1c869a;
    color: #fff;
}

.task-widget__mine-btn--active:hover {
    background: #1c869a;
    border-color: #1c869a;
    color: #fff;
}

/* ── Lista ─────────────────────────────────────────────────────────── */
.task-widget__list {
    max-height: 310px;
    overflow-y: auto;
    flex: 1;
}

.task-widget__state {
    text-align: center;
    padding: 24px 12px;
    color: #888;
    font-size: 13px;
}

.task-widget__item {
    padding: 10px 8px;
    border-bottom: 4px solid #f0f0f0;
    cursor: pointer;
    transition: background 0.15s;
}

.task-widget__item:hover {
    background: #f0f9fb;
}

.task-widget__item:last-child {
    border-bottom: none;
}

.task-widget__item--notify-high {
    border-left: 3px solid #e53935;
}

.task-widget__item--notify-low {
    border-left: 3px solid #f9a825;
}

.task-widget__item-title {
    font-size: 13px;
    font-weight: 600;
    color: #222;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    justify-content: space-between;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.task-widget__reason {
    border-radius: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 1;
    max-width: 150px;
}

.task-widget__reason--itau {
    color: #EC7000;
    border: 1px solid #EC7000;
    font-weight: bold;
    padding: 0px 8px;
}

.task-widget__protocol {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    background: #e0e0e0;
    color: #222;
    font-size: 8px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    white-space: nowrap;
    flex-shrink: 0;
}

.task-widget__item-meta {
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    gap: 4px;
    align-items: center;
    overflow: hidden;
}

.task-widget__badge {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 5px;
}

.task-widget__owner {
    font-size: 11px;
    color: #777;
    border: 1px solid #777;
    border-radius: 5px;
    padding: 1px 8px;
}

.task-widget__sla {
    font-size: 11px;
    font-weight: bold;
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
    width: 20%;
}

.task-widget__sla--ok {
    color: #2e7d32;
}

.task-widget__sla--alert {
    color: #f9a825;
}

.task-widget__sla--expired {
    color: #c62828;
}

/* ── Footer ────────────────────────────────────────────────────────── */
.task-widget__footer {
    padding: 1px 12px;
    border-top: 1px solid #eee;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #fafafa;
}

.task-widget__footer-info {
    font-size: 11px;
    color: #aaa;
}

.task-widget__footer-drag {
    color: #ccc;
    font-size: 13px;
    cursor: grab;
    padding: 2px 6px;
    border-radius: 4px;
    transition: color 0.15s, background 0.15s;
    user-select: none;
}

.task-widget__footer-drag:hover {
    color: #1c869a;
    background: #f0f9fb;
}

.task-widget__footer-drag:active {
    cursor: grabbing;
}

.task-widget__footer-link {
    font-size: 12px;
    color: #1c869a;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
}

.task-widget__footer-link:hover {
    text-decoration: underline;
}

/* ── Paginação ─────────────────────────────────────────────────────── */
.task-widget__pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    padding: 3px 12px;
    border-top: 1px solid #eee;
    background: #fafafa;
}

.task-widget__pagination-controls {
    display: flex;
    align-items: center;
    gap: 6px;
}

.task-widget__page-btn {
    width: 22px;
    height: 22px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: #fff;
    color: #1c869a;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.task-widget__page-btn:hover:not(:disabled) {
    background: #1c869a;
    border-color: #1c869a;
    color: #fff;
}

.task-widget__page-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
}

.task-widget__page-info {
    font-size: 11px;
    color: #666;
    min-width: 40px;
    text-align: center;
}

/* ── Toasts ─────────────────────────────────────────────────────────── */
.task-widget__toasts {
    position: absolute;
    right: calc(100% + 8px);
    bottom: 0;
    display: flex;
    flex-direction: column-reverse;
    gap: 8px;
    pointer-events: none;
    z-index: 10000;
}

.task-widget__toast {
    pointer-events: all;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: #fff;
    border-left: 4px solid #c62828;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    padding: 10px 12px;
    min-width: 220px;
    max-width: 300px;
}

.task-widget__toast-icon {
    color: #c62828;
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 1px;
}

.task-widget__toast-content {
    flex: 1;
    min-width: 0;
}

.task-widget__toast-title {
    font-size: 12px;
    font-weight: 700;
    color: #c62828;
    margin-bottom: 2px;
}

.task-widget__toast-msg {
    font-size: 12px;
    color: #444;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.task-widget__toast-close {
    background: none;
    border: none;
    color: #aaa;
    cursor: pointer;
    font-size: 11px;
    padding: 0;
    flex-shrink: 0;
    line-height: 1;
    margin-top: 2px;
}

.task-widget__toast-close:hover {
    color: #666;
}

.task-toast-enter-active,
.task-toast-leave-active {
    transition: opacity 0.3s, transform 0.3s;
}

.task-toast-enter,
.task-toast-leave-to {
    opacity: 0;
    transform: translateX(16px);
}

/* ── Scrollbar ─────────────────────────────────────────────────────── */
.task-widget__list::-webkit-scrollbar {
    width: 4px;
}

.task-widget__list::-webkit-scrollbar-track {
    background: transparent;
}

.task-widget__list::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
}
</style>
